/**
 * The Edge answer path of an escalation question (agion/Agion#3427 and
 * agion/Agion#3437; owner ruling, Mikko Alasaarela, 2026-10-25): a free-text
 * or form answer goes from the browser to the relay to the runner that asked,
 * and never reaches governance. Governance gives a ticket for the answer; the
 * runner settles it with governance by a keyed hash.
 *
 * This module sends one answer on the status socket and waits for the relay's
 * content-free result. It is generic over the answer kind: free text and
 * form values (PLAN-20261001 slice 20). It keeps no copy of the answer and
 * logs none of it. Single choice keeps its governance route (escalationApi.answer).
 */

import type { EscalationAnswerResult, QuestionReply } from '@agion/mission-stream'
import type { AnswerRefusal, EscalationAnswer } from '../types/escalations'
import { getMissionStatusService } from './streaming/mission-status'

/**
 * How long the browser waits on each step. The status socket connects
 * briefly; the relay's result is due within about 15 s; then governance is
 * read every pollIntervalMs, at most pollAttempts times, until the run has
 * settled the answer. Tests shorten these.
 */
export const relayAnswerTiming = {
  connectTimeoutMs: 5_000,
  resultTimeoutMs: 15_000,
  pollIntervalMs: 1_000,
  pollAttempts: 15,
}

/**
 * Whether an answer's content goes to the run instead of governance: a free
 * text or a form's values ({} included). A single choice does not.
 */
export function answerGoesToRun(answer: EscalationAnswer): boolean {
  return answer.text !== undefined || answer.form_values !== undefined
}

/** The part of the socket this path uses (MissionStatusService). */
export interface EscalationAnswerSocket {
  ensureConnected(timeoutMs: number): Promise<boolean>
  sendEscalationAnswer(frame: { escalation_id: string; ticket: string; answer: QuestionReply }): boolean
  onEscalationAnswerResult(listener: (result: EscalationAnswerResult) => void): () => void
}

/** delivered: the relay handed the answer to the run; otherwise why it did not land. */
export type RelayAnswerOutcome = { delivered: true } | { delivered: false; refusal: AnswerRefusal }

const RELAY_REFUSALS: Record<string, AnswerRefusal> = {
  ticket_mismatch: 'ticket_mismatch',
  ticket_expired: 'ticket_expired',
  run_not_live: 'run_not_live',
  host_not_connected: 'host_not_connected',
  malformed: 'malformed',
}

/** The refusal a relay result names; a reason this browser does not know is a failed send. */
function relayRefusalOf(reason: string | undefined): AnswerRefusal {
  return (reason && RELAY_REFUSALS[reason]) || 'failed'
}

/** The answer's content as the frame carries it, nothing else of the answer body. */
function replyOf(answer: EscalationAnswer): QuestionReply {
  if (answer.form_values !== undefined) return { form_values: answer.form_values }
  return { text: answer.text }
}

/**
 * Connects the status socket for an answer, since no chat needs to be open.
 * Run before the ticket is asked, so a slow connect uses none of its life.
 * Resolves false when it does not connect within the wait.
 */
export function connectForAnswer(socket: EscalationAnswerSocket = getMissionStatusService()): Promise<boolean> {
  return socket.ensureConnected(relayAnswerTiming.connectTimeoutMs)
}

/**
 * Sends the answer with its ticket to the run and resolves with the relay's
 * result for this escalation. A socket that is not connected or no result
 * within the wait is unreachable. Each attempt stops listening when it ends,
 * a timeout included, so a late result for it reaches no one.
 */
export async function sendAnswerToRun(
  escalationId: string,
  ticket: string,
  answer: EscalationAnswer,
  socket: EscalationAnswerSocket = getMissionStatusService(),
): Promise<RelayAnswerOutcome> {
  const unreachable: RelayAnswerOutcome = { delivered: false, refusal: 'unreachable' }

  let stopListening: () => void = () => {}
  let timer: ReturnType<typeof setTimeout> | undefined
  const outcome = new Promise<RelayAnswerOutcome>((resolve) => {
    stopListening = socket.onEscalationAnswerResult((result) => {
      if (result.escalation_id !== escalationId) return
      resolve(
        result.outcome === 'delivered'
          ? { delivered: true }
          : { delivered: false, refusal: relayRefusalOf(result.reason) },
      )
    })
    timer = setTimeout(() => resolve(unreachable), relayAnswerTiming.resultTimeoutMs)
  })

  try {
    if (!socket.sendEscalationAnswer({ escalation_id: escalationId, ticket, answer: replyOf(answer) })) return unreachable
    return await outcome
  } finally {
    clearTimeout(timer)
    stopListening()
  }
}

/** Resolves after ms. */
export function relayPause(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
